import { useEffect, useRef, useState } from "react";
import { Bot, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Berapa harga paket 100 Mbps?",
  "Apa saja syarat order?",
  "Kapan saya bayar fee jasanya?",
  "Apa itu tagihan prorata?",
];

const getSessionId = () => {
  let id = localStorage.getItem("rismacell_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("rismacell_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(getSessionId());
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/chat/${sessionId.current}`)
      .then((r) => r.json())
      .then((data) => setMessages(data))
      .catch(() => {});
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: msg },
      { role: "assistant", content: "" },
    ]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const payload = ev.slice(6);
          if (payload === "[DONE]") break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + parsed.delta,
                };
                return copy;
              });
            } else if (parsed.error) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: parsed.error };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Koneksi bermasalah, coba kirim lagi ya.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Buka asisten chat AI"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full bg-gray-950 flex items-center justify-center shadow-xl hover:scale-110 active:scale-95 transition-transform"
      >
        {open ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Bot className="w-7 h-7 text-white" />
        )}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 left-6 z-50 w-[calc(100vw-3rem)] max-w-sm bg-white rounded-3xl shadow-2xl border border-black/10 flex flex-col overflow-hidden"
          style={{ height: "min(560px, calc(100vh - 8rem))" }}
        >
          <div className="bg-gray-950 px-5 py-4 flex items-center gap-3 shrink-0">
            <span className="w-9 h-9 rounded-full bg-[#E60000] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </span>
            <div>
              <p className="font-heading font-bold text-white text-sm">
                Asisten RismaCell
              </p>
              <p className="text-xs text-gray-400">
                AI siap jawab 24 jam — tanya apa saja
              </p>
            </div>
          </div>

          <div
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          >
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 text-center leading-relaxed">
                  Halo! Tanyakan soal harga paket retensi, fee jasa, atau syarat
                  order.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                      onClick={() => send(s)}
                      className="text-xs font-medium text-[#E60000] bg-red-50 border border-red-100 rounded-full px-3 py-1.5 hover:bg-red-100 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#E60000] text-white rounded-br-md"
                      : "bg-gray-100 text-gray-900 rounded-bl-md"
                  }`}
                >
                  {m.content}
                  {m.role === "assistant" &&
                    streaming &&
                    i === messages.length - 1 &&
                    !m.content && (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="shrink-0 border-t border-black/5 p-3 flex gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Tulis pertanyaanmu..."
              className="flex-1 text-sm bg-gray-100 rounded-full px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#E60000]/30"
              maxLength={1000}
            />
            <button
              type="submit"
              data-testid="chat-send-button"
              disabled={streaming || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#E60000] flex items-center justify-center text-white disabled:opacity-40 hover:bg-red-700 active:scale-95 transition-[background-color,transform,opacity] shrink-0"
              aria-label="Kirim pesan"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
